import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authenticateUser, changePassword as changePasswordDB } from '../services/database';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user';
  password: string;
  createdAt: string;
  first_login?: boolean;
  lastLogin?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isLoading: boolean;
  changePassword: (userId: string, newPassword: string, currentPassword?: string) => Promise<{ success: boolean; error?: string }>;
  changeFirstLoginPassword: (newPassword: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on component mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetch('/api/auth/me', {
          credentials: 'include' // Include cookies in request
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error checking session:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('Login attempt for:', email);
      // Don't set global loading state during login to allow error messages to show
      const result = await authenticateUser(email, password);
      
      console.log('Authentication result:', result);
      
      if (result.user) {
        const { password: _, ...userWithoutPassword } = result.user;
        console.log('Setting user:', userWithoutPassword);
        setUser(userWithoutPassword as User);
        console.log('User authenticated successfully');
        return { success: true };
      }
      
      console.log('Authentication failed:', result.error);
      return { success: false, error: result.error };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'NETWORK_ERROR' };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear session
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setUser(null);
      console.log('User logged out successfully');
    }
  };

  const changePassword = async (userId: string, newPassword: string, currentPassword?: string) => {
    try {
      const result = await changePasswordDB(userId, newPassword, currentPassword);
      if (result.success) {
        console.log('Password updated successfully');
      } else {
        console.error('Failed to update password:', result.error);
      }
      return result;
    } catch (error) {
      console.error('Error changing password:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const changeFirstLoginPassword = async (newPassword: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          newPassword: newPassword
        }),
      });

      if (response.ok) {
        const result = await response.json();
        // Update the user in state to reflect the first_login change
        setUser(prevUser => prevUser ? { ...prevUser, first_login: false } : null);
        console.log('Password changed and first_login updated');
        return true;
      } else {
        console.error('Failed to change password');
        return false;
      }
    } catch (error) {
      console.error('Error changing password:', error);
      return false;
    }
  };

  // Set loading to false after initial load
  useEffect(() => {
    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading, changePassword, changeFirstLoginPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 